(ns bleene.dag.minimap
  (:require
   [react :as react]
   [re-posh.core :as re-posh]
   [applied-science.js-interop :as jsi]
   [react-konva :refer [Rect Group]]
   [re-frame.core :as re-frame]
   [goog.functions :refer [throttle]]
   [bleene.utils :as utils]
   [bleene.dag.dag-context :as dag-context]
   [ui.button :refer [button]]
   ["lucide-react" :refer [ZoomIn ZoomOut RefreshCcw Orbit]]
   ["./dag" :default dag-ng]))


(defn change-scale [stage {:keys [dag-id scale min-scale max-scale scale-fn]}]
  (let [new-scale (scale-fn scale 0.1)]
    (when (< min-scale new-scale max-scale)
      (jsi/call stage :scale #js {:x new-scale :y new-scale})
      (re-frame/dispatch [:dag/set-scale dag-id new-scale]))))


(def minimap-width 300)
(def minimap-height 200)

(defn calc-view-scale [width height]
  (let [initial-scale (/ minimap-width width)
        scaled-height (* height initial-scale)]
    (if (> scaled-height minimap-height)
      (/ minimap-height height)
      initial-scale)))


(defn group-preview [{:keys [dag-id group-id start-x start-y]}]
  (let [{:keys [x y width height]} @(re-frame/subscribe [:dag/group-rect dag-id group-id])]
    [:> Rect {:x      (- x start-x 10)
              :y      (- y start-y 10)
              :width  width
              :height height
              :fill   "rgba(205, 206, 212, 1)"}]))


(defn nodes-preview [{:keys [dag-id start-x start-y]}]
  (let [{:keys [nodes node-width node-height]}
        (utils/query {:nodes           [:dag dag-id {:nodes [:jobs]}]
                      :node-width      [:dag-params :node-width]
                      :node-height     [:dag-params :node-height]})]
    (into
     [:<>]
     (for [{:keys [x y id]} nodes]
       ^{:key id}
       [:> Rect {:x      (- x start-x)
                 :y      (- y start-y)
                 :width  node-width
                 :height node-height
                 :cornerRadius 12
                 :fill   "#fff"}]))))


(defn stage-preview [{:keys [dag-id groups start-x start-y]}]
  [:> Group
   ;; display groups
   (into
    [:<>]
    (for [group-id groups]
      ^{:key group-id}
      [group-preview {:dag-id   dag-id
                      :group-id group-id
                      :start-x  start-x
                      :start-y  start-y}]))
   ;; display nodes
   [nodes-preview {:dag-id  dag-id
                   :start-x start-x
                   :start-y start-y}]])


(defn update-main-stage-position [event {:keys [dag-id scale start-x start-y]}]
  (let [position (jsi/call-in event [:target :getPosition])
        x        (* (+ (jsi/get position :x) start-x) -1 scale)
        y        (* (+ (jsi/get position :y) start-y) -1 scale)]
    (re-frame/dispatch-sync [:dag/set-position dag-id {:x x :y y}])))


(defn update-main-stage-position-click [event {:keys [scale dag-id view-scale half-screen-width half-screen-height]}]
  (let [stage    (jsi/call-in event [:target :getStage])
        position (jsi/call-in stage [:getPointerPosition])
        ex       (jsi/get position :x)
        ey       (jsi/get position :y)
        x        (/ (* -1 ex) view-scale)
        y        (/ (* -1 ey) view-scale)
        x'       (+ half-screen-width (* scale x))
        y'       (+ half-screen-height (* scale y))]
    (re-frame/dispatch-sync [:dag/set-position dag-id {:x x' :y y'}])))


(defn set-position-from-stage [set-position stage]
  (let [rect (jsi/call stage :getClientRect)
        x    (jsi/get rect :x)
        y    (jsi/get rect :y)]
    (set-position {:x x :y y})))


(def throttled-set-position
  (throttle set-position-from-stage 100))


(defn refresh-dag [dag-id]
  (let [{:keys [nodes]} (utils/query {:nodes [:dag dag-id {:nodes [:jobs]}]})
        ids      (map #(or (get-in % [:transform :master-id])
                           (get-in % [:extract :master-id]))
                             nodes)]
    (re-frame/dispatch [:connections.api/fetch-transform-statuses {:ids ids}])
    (re-frame/dispatch-sync [:dag/load-dag-data dag-id])))


(defn refresh-dag-statuses [dag-id]
  (let [{:keys [nodes]} (utils/query {:nodes [:dag dag-id {:nodes [:jobs]}]})
        master-ids      (map #(or (get-in % [:transform :master-id])
                                  (get-in % [:extract :master-id]))
                             nodes)

        loading-statuses? @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-transform-statuses])]

    (when (and (not loading-statuses?) ;; skip fetching statuses if previous request hasn't finished yet
               (not-empty master-ids)) ;; don't bother requesting statuses if no nodes are on the dag
      (re-frame/dispatch [:connections.api/fetch-transform-statuses {:ids master-ids}]))))


(defn minimap [{:keys [stage-ref]}]
  (let [[_ set-position] (react/useState {:x 0 :y 0})

        {:keys [dag-id]} (utils/context dag-context/context)
        stage (jsi/get stage-ref :current)

        container-id (str  "dag-container-" dag-id)]

    (react/useEffect
     (fn []
       (when (some? stage)
         (jsi/call stage :on "dragend x-scale-changed" #(throttled-set-position set-position stage)))

       ;; return unsubscribe callback
       #(when (some? stage)
          (jsi/call stage :off "dragend x-scale-changed")))
     #js [stage])
    [:div {:class "absolute top-3 right-3 flex items-center gap-1"}
     [:div {:class "flex gap-1 bg-kleene-background border rounded-lg p-1"}
      ;; Control buttons

      [button {:variant  :empty
               :title    "Refresh Pipelines"
               :size     :icon-md
               :icon     RefreshCcw
               :on-click (fn refresh []
                           (refresh-dag dag-id))}]

      [button {:variant  :empty
               :title    "Update statuses"
               :size     :icon-md
               :icon     Orbit
               :on-click (fn refresh []
                           (refresh-dag-statuses dag-id))}]

      [button {:variant  :empty
               :title    "Zoom in"
               :size     :icon-md
               :icon     ZoomIn
               :on-click (fn zoom-in []
                           (.zoom dag-ng container-id "in"))}]

      [button {:variant  :empty
               :title    "Zoom out"
               :size     :icon-md
               :icon     ZoomOut
               :on-click (fn zoom-out []
                           (.zoom dag-ng container-id "out"))}]

      #_(when (and open (some? stage))
          ;; Calculate width and height based on nodes
          (let [view-scale (calc-view-scale width height)
                view-dim-x (/ (jsi/call stage :width) scale)
                view-dim-y (/ (jsi/call stage :height) scale)
                view-pos-x (/ (:x position) scale)
                view-pos-y (/ (:y position) scale)
                viewport-x (- (* start-x view-scale) view-pos-x)
                viewport-y (- (* start-y view-scale) view-pos-y)]

            [:> Stage {:width    minimap-width
                       :height   minimap-height
                       :scale-x  view-scale
                       :scale-y  view-scale
                       :on-click (fn [evt]
                                   (update-main-stage-position-click
                                    evt
                                    {:scale              scale
                                     :dag-id             dag-id
                                     :view-scale         view-scale
                                     :half-screen-width  (/ (jsi/get container :width) 2)
                                     :half-screen-height (/ (jsi/get container :height) 2)})

                                   (js/setTimeout
                                    #(throttled-set-position set-position stage)
                                    10))}

             [:> Layer {:id        "preview"
                        :listening false}
              [stage-preview {:dag-id  dag-id
                              :groups  groups
                              :start-x start-x
                              :start-y start-y}]]

             ;; Viewport rect
             [:> Layer {:id "viewport"}
              [:> Rect {:x           (- viewport-x (* 200 view-scale))
                        :y           (- viewport-y (* 30 view-scale))
                        :name        "minimap-viewport-rect"
                        :width       view-dim-x
                        :height      view-dim-y
                        :corner-radius 16
                        :stroke      "rgba(85, 171, 222, 1)"
                        :fill        "rgba(85, 171, 222, 0.15)"
                        :strokeWidth (Math/max (/ 2 view-scale) 2)
                        :draggable   true
                        :on-drag-end (fn [evt]
                                       (update-main-stage-position
                                        evt
                                        {:dag-id  dag-id
                                         :scale   (get-scale)
                                         :start-x start-x
                                         :start-y start-y}))}]]]))]]))
